import type { AutonomyState } from "./autonomy";
import type { Situation } from "./situation";

/**
 * T2, Agent Brains section 10.1 and 12: the run context. What an agent is told
 * about ITSELF at the start of a run: its standing per scenario, the artifact
 * versions it loaded, its own recent record, and the alarms that are up.
 *
 * The types and the renderer are here (pure); the loader that fills a
 * `RunContext` from the database is `packages/db/src/runContext.ts`.
 *
 * The renderer emits codes, numbers and ISO times only (section 10, and the
 * quarantine rule of section 6): nothing stored by a person or a customer can
 * ride into the prompt, because every value is checked against a code shape
 * and replaced by `invalid` when it is not. The block is also wrapped in
 * `quarantine()` by the prompt, so this is two walls, not one.
 *
 * Flag-gated (`ACKRA_RUN_CONTEXT`, default off). Turning it on changes
 * behaviour, so it lands only after a real eval run on Kabir's typed go
 * (CLAUDE.md section 0.5 and section 11).
 */

/**
 * The gateway's `BudgetState` (packages/gateway/src/budget.ts), restated here
 * because core cannot import the gateway (the gateway imports core). The db
 * loader assigns the gateway's value to this type, so a drift is a compile
 * error there.
 */
export type SpendState = "under_cap" | "over_soft_cap" | "over_hard_cap";

export interface StandingView {
  readonly state: AutonomyState | "unknown";
  readonly setBy: string | null;
  readonly setAt: string | null;
  readonly demoted: boolean;
  readonly demotedFrom: AutonomyState | null;
  /** A code from the latest `alert.autonomy_*` row, or null when none was recorded. */
  readonly demotionReason: string | null;
}

export interface RunContext {
  readonly schemaVersion: 1;
  readonly agent: {
    /** T3's `CanvasAgentId`; a plain string here until the roster lands. */
    readonly canvasAgent: string;
    /** T3's `DutyId`; a plain string here until the roster lands. */
    readonly duty: string | null;
    readonly agentId: string;
    readonly agentVersion: string;
  };
  /** Per scenario: the latest standing against the one before it. */
  readonly standings: Readonly<Record<string, StandingView>>;
  /** What this run loaded (the `DraftArtifactVersions` stamp). */
  readonly artifacts: readonly { readonly kind: string; readonly version: number; readonly contentHash: string }[];
  readonly ownStats: {
    readonly periodDays: 14;
    readonly decided: number;
    readonly approvedWithoutEdit: number;
    readonly firstPassClean: number;
    readonly trend: "rising" | "falling" | "flat" | "insufficient_data";
    /** Null until the lessons artifact (W3) exists. */
    readonly openLessonsNamingMe: number | null;
  };
  readonly alarms: {
    readonly threadFrozen: boolean;
    readonly activeAlertKinds: readonly string[];
    readonly budget: SpendState | "unknown";
    /**
     * The OUTBOUND STOP (`outboundStop.ts`), rendered as `outboundStop=`. It is not the
     * autonomy kill switch (`pnpm autonomy kill`), which shows as a scenario's
     * `demotionReason=kill_switch_pulled`; the two were once both called "killSwitch".
     */
    readonly killSwitch: boolean;
  };
}

/** The trusted, constant header. The lines under it are quarantined data. */
export const RUN_CONTEXT_HEADER =
  "[RUN CONTEXT] your standing and this conversation's state; if demoted or alarmed, ask the owner rather than answer when unsure";

/** What a demotion with no recorded alert renders as. Loud on purpose. */
export const DEMOTION_UNRECORDED = "unrecorded";

const CODE = /^[A-Za-z0-9_.:-]{1,80}$/;

/** A code, or the literal `invalid`. Never passes free text through. */
function code(v: string): string {
  return CODE.test(v) ? v : "invalid";
}

const yn = (b: boolean): string => (b ? "yes" : "no");

function isoOrNone(v: string | null): string {
  if (v === null) return "none";
  return Number.isNaN(Date.parse(v)) ? "invalid" : v;
}

export function renderRunContext(
  rc: RunContext,
  situation: Situation | null,
  scenario: string | null,
): readonly string[] {
  const lines: string[] = [];
  const a = rc.agent;
  lines.push(
    `agent id=${code(a.agentId)} version=${code(a.agentVersion)} canvas=${code(a.canvasAgent)} duty=${a.duty === null ? "none" : code(a.duty)}`,
  );

  // The routed scenario's mode line. A scenario with no standing row is
  // "unknown", which the ladder treats as a hold (autonomy_unknown).
  const standing = scenario === null ? undefined : rc.standings[scenario];
  const sv: StandingView =
    standing ?? { state: "unknown", setBy: null, setAt: null, demoted: false, demotedFrom: null, demotionReason: null };
  let modeLine = `scenario=${scenario === null ? "none" : code(scenario)} mode=${code(sv.state)} demoted=${yn(sv.demoted)}`;
  if (sv.demoted) {
    const reason = sv.demotionReason !== null && CODE.test(sv.demotionReason) ? sv.demotionReason : DEMOTION_UNRECORDED;
    modeLine += ` demotedFrom=${sv.demotedFrom === null ? "unknown" : code(sv.demotedFrom)} demotionReason=${reason}`;
  }
  modeLine += ` setAt=${isoOrNone(sv.setAt)}`;
  lines.push(modeLine);

  const demoted = Object.entries(rc.standings)
    .filter(([, v]) => v.demoted)
    .map(([k]) => code(k))
    .sort();
  if (demoted.length > 0) lines.push(`demotedScenarios=${demoted.join(",")}`);

  for (const art of [...rc.artifacts].sort((x, y) => (x.kind < y.kind ? -1 : x.kind > y.kind ? 1 : 0))) {
    lines.push(`artifact ${code(art.kind)} v${art.version} hash=${code(art.contentHash.slice(0, 12))}`);
  }

  const s = rc.ownStats;
  lines.push(
    `stats periodDays=${s.periodDays} decided=${s.decided} approvedWithoutEdit=${s.approvedWithoutEdit} firstPassClean=${s.firstPassClean} trend=${code(s.trend)} openLessons=${s.openLessonsNamingMe === null ? "unknown" : s.openLessonsNamingMe}`,
  );

  const al = rc.alarms;
  lines.push(
    `alarms threadFrozen=${yn(al.threadFrozen)} budget=${code(al.budget)} outboundStop=${yn(al.killSwitch)} alertKinds=${al.activeAlertKinds.length === 0 ? "none" : al.activeAlertKinds.map(code).join(",")}`,
  );

  if (situation !== null) {
    lines.push(
      `situation stage=${code(situation.stage.value)} explicit=${yn(situation.stage.explicit)} window=${code(situation.window.state)} closesAt=${isoOrNone(situation.window.closesAt)} frozen=${yn(situation.frozen)}`,
    );
    lines.push(
      `promises=${situation.promises.length} pendingActions=${situation.pendingActions.length} openEscalations=${situation.openEscalations.length} unhandledKinds=${situation.unhandledKinds}`,
    );
    for (const p of situation.promises) {
      lines.push(`promise ${code(p.kind)} ${code(p.status)} by=${isoOrNone(p.byWhen)}`);
    }
    for (const act of situation.pendingActions) {
      const who = act.owner.type === "agent" ? code(act.owner.agentId) : "owner";
      lines.push(`pending ${code(act.kind)} owner=${who} ref=${code(act.ref)} since=${isoOrNone(act.since)} due=${isoOrNone(act.dueAt)}`);
    }
    if (situation.lastAgent !== null) {
      lines.push(`lastAgent ${code(situation.lastAgent.agentId)} ${code(situation.lastAgent.eventType)} at=${isoOrNone(situation.lastAgent.at)}`);
    }
  }
  return lines;
}
